import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "@/components/Dashboard/DashboardLayout";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Users,
  Shield,
  History,
  Search,
  Plus,
  Edit,
  Trash2,
  UserCheck,
  UserX,
  Filter,
  Eye,
  Settings,
  Key,
  Activity,
} from "lucide-react";
import { UserFormDialog } from "@/components/UserManagement/UserFormDialog";
import { RolesPermissionsView } from "@/components/UserManagement/RolesPermissionsView";
import { AuditLogView } from "@/components/UserManagement/AuditLogView";
import { UserDetailsDialog } from "@/components/UserManagement/UserDetailsDialog";
import { ExportUsersButton } from "@/components/UserManagement/ExportUsersButton";
import { ResetPasswordDialog } from "@/components/UserManagement/ResetPasswordDialog";
import { UserActivityLog } from "@/components/UserManagement/UserActivityLog";
import { AdvancedFilters, type UserFilters, defaultFilters } from "@/components/UserManagement/AdvancedFilters";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { cn } from "@/lib/utils";

interface User {
  id: string;
  email: string;
  full_name: string | null;
  is_active: boolean;
  created_at: string;
  profiles?: {
    full_name: string | null;
    phone: string | null;
    department: string | null;
    position: string | null;
    employee_id: string | null;
    tag: string | null;
    area: string | null;
    hire_date: string | null;
    last_login: string | null;
    login_count: number;
    allowed_modules?: string[] | null;
    skill_level?: 'A' | 'B' | 'C' | null;
  };
  user_roles?: {
    role: string;
  }[];
}

export default function UserManagementAdvanced() {
  const { userRole } = useAuth();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("users");
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [viewingUser, setViewingUser] = useState<User | null>(null);
  // Nuevos estados para las funcionalidades adicionales
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState<User | null>(null);
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [activityUser, setActivityUser] = useState<User | null>(null);
  const [advancedFilters, setAdvancedFilters] = useState<UserFilters>(defaultFilters);

  const { data: users, isLoading } = useQuery({
    queryKey: ["users-advanced"],
    queryFn: async () => {
      // Fetch profiles (which contain user data)
      const { data: profiles, error: profilesError } = await api
        .from("profiles")
        .select("*")
        .order("created_at", { ascending: false });

      if (profilesError) throw profilesError;

      // Fetch user roles
      const { data: roles, error: rolesError } = await api
        .from("user_roles")
        .select("user_id, role");

      if (rolesError) throw rolesError;

      // Combine data - map profiles to the expected User format
      const combinedUsers = profiles?.map((profile: any) => {
        // Build a reasonable email display - use actual email if exists, otherwise construct from available data
        const displayEmail = profile.email ||
          (profile.employee_id ? `${profile.employee_id}@magma.local` :
          (profile.tag ? `${profile.tag}@magma.local` :
          `${profile.full_name?.toLowerCase().replace(/\s+/g, '.') || 'usuario'}@magma.local`));

        return {
          id: profile.id,
          email: displayEmail,
          full_name: profile.full_name,
          is_active: profile.is_active ?? true,
          created_at: profile.created_at,
          profiles: {
            full_name: profile.full_name,
            phone: profile.phone,
            department: profile.department,
            position: profile.position,
            employee_id: profile.employee_id,
            tag: profile.tag,
            area: profile.area,
            hire_date: profile.hire_date,
            last_login: profile.last_login,
            login_count: profile.login_count || 0,
            allowed_modules: profile.allowed_modules,
            skill_level: profile.skill_level,
            shift: profile.shift,
          },
          user_roles: roles?.filter((r: any) => r.user_id === profile.id).map((r: any) => ({ role: r.role })) || [],
        };
      });

      return combinedUsers as User[];
    },
  });

  const toggleUserStatus = useMutation({
    mutationFn: async ({ userId, isActive }: { userId: string; isActive: boolean }) => {
      const { error } = await api
        .from("users")
        .update({ is_active: isActive })
        .eq("id", userId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users-advanced"] });
      toast.success("Estado del usuario actualizado");
    },
    onError: (error: any) => {
      toast.error(error.message || "Error al actualizar usuario");
    },
  });

  const filteredUsers = users?.filter((user) => {
    const matchesSearch =
      searchTerm === "" ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.profiles?.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.profiles?.employee_id?.toLowerCase().includes(searchTerm.toLowerCase());

    const userRole = user.user_roles?.[0]?.role || "viewer";
    const matchesRole = roleFilter === "all" || userRole === roleFilter;

    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "active" && user.is_active) ||
      (statusFilter === "inactive" && !user.is_active);

    return matchesSearch && matchesRole && matchesStatus;
  });

  const stats = {
    total: users?.length || 0,
    active: users?.filter((u) => u.is_active).length || 0,
    inactive: users?.filter((u) => !u.is_active).length || 0,
    admins: users?.filter((u) => u.user_roles?.[0]?.role === "admin").length || 0,
  };

  const getRoleBadge = (role: string) => {
    const config: Record<string, { label: string; color: string }> = {
      admin: { label: "Administrador", color: "bg-red-500/10 text-red-500" },
      supervisor: { label: "Supervisor", color: "bg-purple-500/10 text-purple-500" },
      operator: { label: "Operador", color: "bg-blue-500/10 text-blue-500" },
      quality: { label: "Calidad", color: "bg-green-500/10 text-green-500" },
      warehouse: { label: "Almacen", color: "bg-orange-500/10 text-orange-500" },
      maintenance: { label: "Mantenimiento", color: "bg-cyan-500/10 text-cyan-500" },
      viewer: { label: "Visor", color: "bg-gray-500/10 text-gray-500" },
    };
    const { label, color } = config[role] || config.viewer;
    return <Badge className={color}>{label}</Badge>;
  };

  const getSkillLevelBadge = (level: 'A' | 'B' | 'C' | null | undefined) => {
    if (!level) return null;
    const config: Record<string, { label: string; color: string; title: string }> = {
      A: { label: "A", color: "bg-emerald-500/10 text-emerald-600 border-emerald-300", title: "Experto - Tareas de alta complejidad" },
      B: { label: "B", color: "bg-amber-500/10 text-amber-600 border-amber-300", title: "Intermedio - Tareas estándar" },
      C: { label: "C", color: "bg-slate-500/10 text-slate-600 border-slate-300", title: "Básico - Tareas simples" },
    };
    const { label, color, title } = config[level] || { label: level, color: "bg-gray-500/10", title: "" };
    return <Badge className={cn(color, "text-xs font-bold border")} title={title}>{label}</Badge>;
  };

  const canManage = userRole === "admin";

  const handleEdit = (user: User) => {
    setSelectedUser(user);
    setDialogOpen(true);
  };

  const handleNew = () => {
    setSelectedUser(null);
    setDialogOpen(true);
  };

  const handleViewDetails = (user: User) => {
    setViewingUser(user);
    setDetailsDialogOpen(true);
  };

  const handleResetPassword = (user: User) => {
    setResetPasswordUser(user);
    setResetPasswordDialogOpen(true);
  };

  const handleViewActivity = (user: User) => {
    setActivityUser(user);
    setActivityDialogOpen(true);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Gestión de Usuarios</h1>
            <p className="text-sm sm:text-base text-muted-foreground mt-1">
              Administración de usuarios, roles y permisos
            </p>
          </div>
          {canManage && (
            <Button onClick={handleNew} className="w-full sm:w-auto">
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Usuario
            </Button>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full max-w-lg grid-cols-3 h-auto">
            <TabsTrigger value="users" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm py-2">
              <Users className="w-4 h-4" />
              <span className="hidden xs:inline">Usuarios</span>
            </TabsTrigger>
            <TabsTrigger value="roles" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm py-2">
              <Shield className="w-4 h-4" />
              <span className="hidden xs:inline">Roles</span>
              <span className="hidden sm:inline"> y Permisos</span>
            </TabsTrigger>
            <TabsTrigger value="audit" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm py-2">
              <History className="w-4 h-4" />
              <span className="hidden xs:inline">Auditoría</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="users" className="mt-6 space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
              <Card>
                <CardContent className="p-3 sm:pt-4 sm:p-4">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="p-1.5 sm:p-2 rounded-lg bg-blue-500/10">
                      <Users className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />
                    </div>
                    <div>
                      <p className="text-xl sm:text-2xl font-bold">{stats.total}</p>
                      <p className="text-xs sm:text-sm text-muted-foreground">Total</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3 sm:pt-4 sm:p-4">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="p-1.5 sm:p-2 rounded-lg bg-green-500/10">
                      <UserCheck className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />
                    </div>
                    <div>
                      <p className="text-xl sm:text-2xl font-bold text-green-600">{stats.active}</p>
                      <p className="text-xs sm:text-sm text-muted-foreground">Activos</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3 sm:pt-4 sm:p-4">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="p-1.5 sm:p-2 rounded-lg bg-gray-500/10">
                      <UserX className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
                    </div>
                    <div>
                      <p className="text-xl sm:text-2xl font-bold">{stats.inactive}</p>
                      <p className="text-xs sm:text-sm text-muted-foreground">Inactivos</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3 sm:pt-4 sm:p-4">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="p-1.5 sm:p-2 rounded-lg bg-red-500/10">
                      <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-red-500" />
                    </div>
                    <div>
                      <p className="text-xl sm:text-2xl font-bold">{stats.admins}</p>
                      <p className="text-xs sm:text-sm text-muted-foreground">Admins</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Filters */}
            <div className="space-y-3">
              {/* Search - Full width on mobile */}
              <div className="relative w-full">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nombre, email o ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 w-full"
                />
              </div>
              
              {/* Filter controls */}
              <div className="flex flex-wrap items-center gap-2">
                <Select value={roleFilter} onValueChange={setRoleFilter}>
                  <SelectTrigger className="w-[130px] sm:w-[160px]">
                    <Filter className="w-4 h-4 mr-1 sm:mr-2 flex-shrink-0" />
                    <SelectValue placeholder="Rol" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los roles</SelectItem>
                    <SelectItem value="admin">Administrador</SelectItem>
                    <SelectItem value="supervisor">Supervisor</SelectItem>
                    <SelectItem value="operator">Operador</SelectItem>
                    <SelectItem value="quality">Calidad</SelectItem>
                    <SelectItem value="warehouse">Almacen</SelectItem>
                    <SelectItem value="maintenance">Mantenimiento</SelectItem>
                    <SelectItem value="viewer">Visor</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[110px] sm:w-[140px]">
                    <SelectValue placeholder="Estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="active">Activos</SelectItem>
                    <SelectItem value="inactive">Inactivos</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-2 ml-auto">
                  <AdvancedFilters
                    filters={advancedFilters}
                    onFiltersChange={setAdvancedFilters}
                    onReset={() => setAdvancedFilters(defaultFilters)}
                  />
                  <ExportUsersButton users={filteredUsers} isLoading={isLoading} />
                </div>
              </div>
            </div>

            {/* Users List - Mobile Cards / Desktop Table */}
            {isLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
              </div>
            ) : filteredUsers && filteredUsers.length > 0 ? (
              <>
                {/* Mobile View - Cards */}
                <div className="grid gap-3 md:hidden">
                  {filteredUsers.map((user) => (
                    <Card key={user.id} className="overflow-hidden">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                              <span className="text-lg font-bold text-primary">
                                {(user.profiles?.full_name || user.email)?.[0]?.toUpperCase()}
                              </span>
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="font-semibold truncate">
                                {user.profiles?.full_name || user.full_name || "Sin nombre"}
                              </p>
                              <p className="text-sm text-muted-foreground truncate">{user.email}</p>
                            </div>
                          </div>
                          <Badge
                            className={cn(
                              "flex-shrink-0",
                              user.is_active
                                ? "bg-green-500/10 text-green-500"
                                : "bg-gray-500/10 text-gray-500"
                            )}
                          >
                            {user.is_active ? "Activo" : "Inactivo"}
                          </Badge>
                        </div>
                        
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          {getRoleBadge(user.user_roles?.[0]?.role || "viewer")}
                          {canManage && getSkillLevelBadge(user.profiles?.skill_level)}
                          {user.profiles?.department && (
                            <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
                              {user.profiles.department}
                            </span>
                          )}
                        </div>
                        
                        <div className="mt-3 text-xs text-muted-foreground">
                          Último acceso: {user.profiles?.last_login
                            ? format(new Date(user.profiles.last_login), "dd/MM/yyyy HH:mm", { locale: es })
                            : "Nunca"}
                        </div>
                        
                        <div className="mt-3 pt-3 border-t flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewDetails(user)}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Ver
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewActivity(user)}
                          >
                            <Activity className="w-4 h-4 mr-1" />
                            Actividad
                          </Button>
                          {canManage && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <Settings className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleEdit(user)}>
                                  <Edit className="w-4 h-4 mr-2" />
                                  Editar
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleResetPassword(user)}>
                                  <Key className="w-4 h-4 mr-2 text-amber-500" />
                                  Resetear contraseña
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => toggleUserStatus.mutate({ userId: user.id, isActive: !user.is_active })}
                                >
                                  {user.is_active ? (
                                    <>
                                      <UserX className="w-4 h-4 mr-2 text-red-500" />
                                      Desactivar
                                    </>
                                  ) : (
                                    <>
                                      <UserCheck className="w-4 h-4 mr-2 text-green-500" />
                                      Activar
                                    </>
                                  )}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Desktop View - Table */}
                <Card className="hidden md:block">
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b bg-muted/50">
                            <th className="text-left p-4 font-medium">Usuario</th>
                            <th className="text-left p-4 font-medium">Rol</th>
                            <th className="text-left p-4 font-medium hidden lg:table-cell">Departamento</th>
                            <th className="text-left p-4 font-medium">Estado</th>
                            <th className="text-left p-4 font-medium hidden lg:table-cell">Último Acceso</th>
                            <th className="text-right p-4 font-medium">Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredUsers.map((user) => (
                            <tr key={user.id} className="border-b hover:bg-muted/30">
                              <td className="p-4">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                    <span className="text-sm font-bold text-primary">
                                      {(user.profiles?.full_name || user.email)?.[0]?.toUpperCase()}
                                    </span>
                                  </div>
                                  <div>
                                    <p className="font-medium">
                                      {user.profiles?.full_name || user.full_name || "Sin nombre"}
                                    </p>
                                    <p className="text-sm text-muted-foreground">{user.email}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="p-4">
                                <div className="flex items-center gap-2">
                                  {getRoleBadge(user.user_roles?.[0]?.role || "viewer")}
                                  {canManage && getSkillLevelBadge(user.profiles?.skill_level)}
                                </div>
                              </td>
                              <td className="p-4 hidden lg:table-cell">
                                <span className="text-sm">
                                  {user.profiles?.department || "-"}
                                </span>
                              </td>
                              <td className="p-4">
                                <Badge
                                  className={cn(
                                    user.is_active
                                      ? "bg-green-500/10 text-green-500"
                                      : "bg-gray-500/10 text-gray-500"
                                  )}
                                >
                                  {user.is_active ? "Activo" : "Inactivo"}
                                </Badge>
                              </td>
                              <td className="p-4 hidden lg:table-cell">
                                <span className="text-sm text-muted-foreground">
                                  {user.profiles?.last_login
                                    ? format(new Date(user.profiles.last_login), "dd/MM/yyyy HH:mm", {
                                        locale: es,
                                      })
                                    : "Nunca"}
                                </span>
                              </td>
                              <td className="p-4">
                                <div className="flex items-center justify-end gap-1">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleViewDetails(user)}
                                    title="Ver detalles"
                                  >
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleViewActivity(user)}
                                    title="Ver actividad"
                                  >
                                    <Activity className="w-4 h-4" />
                                  </Button>
                                  {canManage && (
                                    <>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => handleEdit(user)}
                                        title="Editar"
                                      >
                                        <Edit className="w-4 h-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => handleResetPassword(user)}
                                        title="Resetear contraseña"
                                      >
                                        <Key className="w-4 h-4 text-amber-500" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() =>
                                          toggleUserStatus.mutate({
                                            userId: user.id,
                                            isActive: !user.is_active,
                                          })
                                        }
                                        title={user.is_active ? "Desactivar" : "Activar"}
                                      >
                                        {user.is_active ? (
                                          <UserX className="w-4 h-4 text-red-500" />
                                        ) : (
                                          <UserCheck className="w-4 h-4 text-green-500" />
                                        )}
                                      </Button>
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <Users className="w-12 h-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">
                    {searchTerm || roleFilter !== "all" || statusFilter !== "all"
                      ? "No se encontraron usuarios con los filtros seleccionados"
                      : "No hay usuarios registrados"}
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="roles" className="mt-6">
            <RolesPermissionsView />
          </TabsContent>

          <TabsContent value="audit" className="mt-6">
            <AuditLogView />
          </TabsContent>
        </Tabs>
      </div>

      <UserFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        user={selectedUser}
        onSuccess={() => queryClient.invalidateQueries({ queryKey: ["users-advanced"] })}
      />

      {viewingUser && (
        <UserDetailsDialog
          open={detailsDialogOpen}
          onOpenChange={setDetailsDialogOpen}
          user={viewingUser}
        />
      )}

      <ResetPasswordDialog
        open={resetPasswordDialogOpen}
        onOpenChange={setResetPasswordDialogOpen}
        user={resetPasswordUser}
        onSuccess={() => queryClient.invalidateQueries({ queryKey: ["users-advanced"] })}
      />

      <UserActivityLog
        open={activityDialogOpen}
        onOpenChange={setActivityDialogOpen}
        user={activityUser}
      />
    </DashboardLayout>
  );
}
